/* Added the following code for FAST.Ray using AI Agent. 27/07/2026. Code Start */
/**
 * Stable accordion ids for lesson Blocks ↔ YOUR PROGRESS jump-to-section.
 * Prefer data-accordion-id over display headerText so label drift cannot break navigation.
 */

/** Display header → checklist / data-accordion-id */
const HEADER_TO_ACCORDION_ID = {
  "what is it?": "what_is_it",
  "what is it": "what_is_it",
  video: "video",
  "test it": "test_it",
  "try it": "try_it",
  quiz: "quiz",
  "check it": "check_it",
};

/**
 * Normalize a Blocks.jsx headerText into a stable accordion id.
 */
export function headerTextToAccordionId(headerText) {
  if (!headerText || typeof headerText !== "string") return null;
  const key = headerText.trim().toLowerCase().replace(/\s+/g, " ");
  return HEADER_TO_ACCORDION_ID[key] || key.replace(/\s+/g, "_");
}

/**
 * Prefer API accordionId, then checklist item.id.
 */
export function checklistItemAccordionId(item) {
  if (!item) return null;
  if (item.accordionId) return String(item.accordionId);
  if (!item.id) return null;
  const id = String(item.id).toLowerCase();
  return id === "visit" ? "what_is_it" : id;
}

/**
 * Learner-facing copy for the next incomplete checklist activity.
 */
export function activityGuidance(item) {
  if (!item) return "All required activities are complete.";
  const threshold = item.requiredPercent || item.minPercent || item.threshold;
  const current = item.percent ?? item.progressPercent ?? item.scorePercent;
  if (current != null && threshold != null) {
    return `${item.label}: ${current}% complete; ${threshold}% required.`;
  }
  if (item.id === "quiz") return "Take or retry the quiz to reach the required score.";
  if (item.id === "video") return "Watch the required portion of the video.";
  if (item.id === "try_it") return "Run the Try it exercise successfully.";
  if (item.id === "check_it") return "Open the Check it section to continue.";
  /* Added the following code for video.ray using AI Agent. 30/07/2026. Code Start */
  if (String(item.id || "").startsWith("hub_visit:")) {
    return "Open this part overview to continue the learning path.";
  }
  if (String(item.id || "").startsWith("hub_video:")) {
    return "Watch this part video to the required threshold.";
  }
  /* Code end. ray using AI Agent. 30/07/2026 */
  return `Complete ${item.label} to continue.`;
}

/* Added the following code for menu.ray using AI Agent. 28/07/2026. Code Start */
/**
 * Short primary CTA when the learner still owes a checklist activity.
 */
export function finishActivityCtaLabel(item) {
  if (!item) return "Finish checklist";
  const id = String(item.id || "").toLowerCase();
  const byId = {
    video: "Finish: Watch video",
    quiz: "Finish: Take quiz",
    try_it: "Finish: Try it",
    check_it: "Finish: Check it",
    visit: "Finish: Read section",
    what_is_it: "Finish: What is it?",
    test_it: "Finish: Test it",
  };
  if (byId[id]) return byId[id];
  const label = String(item.label || "activity").trim();
  return label ? `Finish: ${label}` : "Finish checklist";
}

/** Compact activity-type chip for destination preview. */
export function activityTypeChip(item) {
  if (!item) return null;
  const id = String(item.id || "").toLowerCase();
  const byId = {
    video: "Video",
    quiz: "Quiz",
    try_it: "Try it",
    check_it: "Check it",
    visit: "Read",
    what_is_it: "Read",
    test_it: "Test it",
  };
  return byId[id] || item.label || null;
}

/** Keep button labels readable when menu titles are long. */
export function shortTopicLabel(label, maxLen = 36) {
  const text = String(label || "Next topic").trim();
  if (text.length <= maxLen) return text;
  return `${text.slice(0, Math.max(1, maxLen - 1)).trimEnd()}…`;
}

/**
 * Honest copy for why Next is not available yet (checklist gap vs path lock).
 */
export function explainNextTopicLock({
  checklistComplete,
  incomplete = [],
  nextTopic,
  sectionPages = [],
  currentPage,
  lockMap,
  completionPolicy,
} = {}) {
  if (!checklistComplete) {
    const nextItem = incomplete[0];
    if (nextItem) return activityGuidance(nextItem);
    return (
      completionPolicy?.note ||
      "Finish every checklist item on this page before leaving the lesson."
    );
  }

  if (!nextTopic?.locked) return null;

  const missing =
    Array.isArray(lockMap?.missingChecklist) && lockMap.missingChecklist.length
      ? lockMap.missingChecklist.map((i) => i.label || i.id).filter(Boolean).join(", ")
      : "";
  if (lockMap?.lockMessage) {
    return missing
      ? `${lockMap.lockMessage} Still needed: ${missing}.`
      : lockMap.lockMessage;
  }
  if (missing) {
    return `Still needed before unlock: ${missing}.`;
  }

  const pages = Array.isArray(sectionPages) ? sectionPages : [];
  const nextIdx = nextTopic.unitKey
    ? pages.findIndex((p) => p.unitKey === nextTopic.unitKey)
    : -1;
  if (nextIdx > 0) {
    const prev = pages[nextIdx - 1];
    if (prev && prev.state !== "done" && prev.unitKey !== currentPage?.unitKey) {
      return `Complete “${prev.label}” first — topics unlock in order.`;
    }
  }

  const currentInSection = currentPage?.unitKey
    ? pages.find((p) => p.unitKey === currentPage.unitKey)
    : null;
  if (
    currentInSection &&
    currentInSection.state !== "done" &&
    currentInSection.status !== "completed"
  ) {
    return "This module is still saving completion — wait a moment or refresh progress so the next topic can unlock.";
  }

  return (
    completionPolicy?.note ||
    "This topic is locked by a learning-path rule. Open Learning path for details."
  );
}
/* Code end. ray using AI Agent. 28/07/2026 */

/**
 * Whether checklist is fully complete (empty checklist = nothing required).
 */
export function isChecklistComplete(checklist) {
  if (!Array.isArray(checklist) || checklist.length === 0) return true;
  return checklist.every((c) => c.done);
}

/* Added the following code for progress.ray using AI Agent. 28/07/2026. Code Start */
/** Accordion id → Blocks headerText used when DOM lookup by id fails. */
const ACCORDION_ID_TO_HEADER = {
  what_is_it: "What is it?",
  video: "Video",
  test_it: "Test it",
  try_it: "Try it",
  quiz: "Quiz",
  check_it: "Check it",
};

/**
 * Scroll to / open the lesson accordion matching a checklist activity id.
 * Returns true when the accordion header was found in the DOM.
 */
export function jumpToAccordion(accordionId, fallbackLabel) {
  if (typeof document === "undefined") return false;
  if (!accordionId && !fallbackLabel) return false;

  const id = accordionId ? String(accordionId) : null;
  const byId = id
    ? document.querySelector(`[data-accordion-id="${CSS.escape(id)}"]`)
    : null;

  const headerCandidates = [
    fallbackLabel,
    id ? ACCORDION_ID_TO_HEADER[id] : null,
  ].filter(Boolean);

  let byHeader = null;
  for (const label of headerCandidates) {
    byHeader = document.querySelector(
      `[data-accordion-header="${CSS.escape(String(label))}"]`
    );
    if (byHeader) break;
  }

  const el = byId || byHeader;
  if (!el) return false;

  el.scrollIntoView({ behavior: "smooth", block: "center" });
  const accordion = el.closest(".MuiAccordion-root");
  const alreadyOpen = accordion?.classList?.contains("Mui-expanded");
  if (!alreadyOpen) {
    // Prefer clicking the summary so controlled MUI Accordions receive onChange.
    const summary =
      el.getAttribute?.("data-accordion-id") ||
      el.getAttribute?.("data-accordion-header")
        ? el
        : accordion?.querySelector(".MuiAccordionSummary-root") || el;
    summary.click();
  }
  return true;
}

/* Added the following code for menu.ray using AI Agent. 28/07/2026. Code Start */
/**
 * Open the lesson header video panel and scroll the player into view.
 * Most topics mount CustomVideoPlayer only after "Watch Video" is clicked.
 */
/* Added the following code for video.ray using AI Agent. 30/07/2026. Code Start */
function forceVideoAutoplayStorageKey(pageSlug) {
  const slug = String(pageSlug || "")
    .trim()
    .replace(/^\/+/, "")
    .toLowerCase();
  return slug ? `selftrainai:force-video-autoplay:${slug}` : "";
}

/** Mark the next video mount/open on this page to start muted playback. */
export function requestLessonVideoAutoplay(pageSlug) {
  if (typeof sessionStorage === "undefined") return;
  const key = forceVideoAutoplayStorageKey(pageSlug);
  if (!key) return;
  sessionStorage.setItem(key, "1");
}

/** True once if a sidebar/resume navigation asked this page to autoplay. */
export function consumeLessonVideoAutoplayRequest(pageSlug) {
  if (typeof sessionStorage === "undefined") return false;
  const key = forceVideoAutoplayStorageKey(pageSlug);
  if (!key) return false;
  if (sessionStorage.getItem(key) !== "1") return false;
  sessionStorage.removeItem(key);
  return true;
}

async function tryMutedInlinePlay(videoEl) {
  if (!videoEl) return false;
  try {
    videoEl.muted = true;
    videoEl.defaultMuted = true;
    videoEl.playsInline = true;
    await videoEl.play();
    return true;
  } catch (err) {
    console.warn("muted video autoplay blocked:", err);
    return false;
  }
}
/* Code end. ray using AI Agent. 30/07/2026 */

export function openPendingLessonVideo(options = {}) {
  if (typeof document === "undefined") return false;

  /* Added the following code for video.ray using AI Agent. 30/07/2026. Code Start */
  /* Added the following code for testit.ray using AI Agent. 2026-08-07. Code Start */
  // No autostart: open panel only; never request muted force-play.
  const autoplay = false;
  /* Code end. ray using AI Agent. 2026-08-07 */
  /* Commenting the following code for testit.ray using AI Agent. 2026-08-07. Comment Start */
  // const autoplay = !!options.autoplay;
  /* Commenting end. ray using AI Agent. 2026-08-07 */
  const pageSlug = options.pageSlug || "";
  if (autoplay && pageSlug) {
    requestLessonVideoAutoplay(pageSlug);
  }
  /* Code end. ray using AI Agent. 30/07/2026 */

  const closeBtn = document.querySelector('[title="Close Video"]');
  const openBtn = document.querySelector('[title="Watch Video"]');

  if (openBtn) {
    openBtn.click();
  } else if (!closeBtn) {
    return false;
  }

  const scrollPlayer = () => {
    const player =
      document.querySelector("video") ||
      document.querySelector('[class*="VideoPlayer"]') ||
      document.querySelector('[title="Close Video"]');
    player?.scrollIntoView?.({ behavior: "smooth", block: "center" });
  };
  scrollPlayer();
  if (typeof window !== "undefined") {
    window.setTimeout(scrollPlayer, 150);
    /* Added the following code for video.ray using AI Agent. 30/07/2026. Code Start */
    // Sidebar "Next video" / resume: force muted play after the panel mounts.
    if (autoplay) {
      const attemptPlay = () => {
        const video = document.querySelector("video");
        if (!video) return false;
        tryMutedInlinePlay(video);
        window.dispatchEvent(
          new CustomEvent("selftrainai:force-video-autoplay", {
            detail: { pageSlug },
          })
        );
        return true;
      };
      if (!attemptPlay()) {
        window.setTimeout(attemptPlay, 200);
        window.setTimeout(attemptPlay, 500);
        window.setTimeout(attemptPlay, 1000);
      }
    }
    /* Code end. ray using AI Agent. 30/07/2026 */
  }
  return true;
}

/**
 * Jump to the pending checklist activity. Video opens the header player;
 * other activities use accordion ids.
 */
export function jumpToChecklistActivity(item, options = {}) {
  if (!item) return false;
  const id = String(item.id || "").toLowerCase();
  const accordionId = checklistItemAccordionId(item);

  if (id === "video") {
    if (
      openPendingLessonVideo({
        autoplay: !!options.autoplay,
        pageSlug: options.pageSlug,
      })
    ) {
      return true;
    }
    // Fallback for rare pages that use a Video accordion instead of header toggle.
    return jumpToAccordion(accordionId || "video", item.label || "Video");
  }

  return jumpToAccordion(accordionId, item.label);
}

/**
 * Retry jump until the lesson UI mounts (lazy pages / delayed video control).
 */
export function jumpToChecklistActivityWithRetry(
  item,
  { attempts = 12, delayMs = 120, autoplay = false, pageSlug = "" } = {}
) {
  return new Promise((resolve) => {
    if (typeof window === "undefined" || !item) {
      resolve(false);
      return;
    }
    let n = 0;
    const tick = () => {
      if (jumpToChecklistActivity(item, { autoplay, pageSlug })) {
        resolve(true);
        return;
      }
      n += 1;
      if (n >= attempts) {
        resolve(false);
        return;
      }
      window.setTimeout(tick, delayMs);
    };
    tick();
  });
}
/* Code end. ray using AI Agent. 28/07/2026 */

/**
 * Retry jump until the lesson accordions mount (route change / lazy page).
 */
export function jumpToAccordionWithRetry(
  accordionId,
  fallbackLabel,
  { attempts = 12, delayMs = 120 } = {}
) {
  return new Promise((resolve) => {
    if (typeof window === "undefined") {
      resolve(false);
      return;
    }
    let n = 0;
    const tick = () => {
      if (jumpToAccordion(accordionId, fallbackLabel)) {
        resolve(true);
        return;
      }
      n += 1;
      if (n >= attempts) {
        resolve(false);
        return;
      }
      window.setTimeout(tick, delayMs);
    };
    tick();
  });
}
/* Code end. ray using AI Agent. 28/07/2026 */

/**
 * Client fallback when /me payload lacks server nextTopic (older responses).
 */
export function resolveNextTopicClient({
  sectionPages = [],
  currentPage,
  resume,
} = {}) {
  const currentKey = currentPage?.unitKey || null;
  const currentPath = currentPage?.path || null;

  if (Array.isArray(sectionPages) && sectionPages.length > 0 && currentKey) {
    const idx = sectionPages.findIndex((p) => p.unitKey === currentKey);
    if (idx >= 0) {
      const following = sectionPages.slice(idx + 1);
      const unlocked = following.find((p) => p.state !== "locked" && p.path);
      if (unlocked) {
        return {
          unitKey: unlocked.unitKey,
          label: unlocked.label,
          path: unlocked.path,
          locked: false,
        };
      }
      const lockedNext = following.find((p) => p.path);
      if (lockedNext) {
        return {
          unitKey: lockedNext.unitKey,
          label: lockedNext.label,
          path: lockedNext.path,
          locked: true,
        };
      }
    }
  }

  const resumePath = resume?.path || null;
  const resumeKey =
    resume?.pageSlug ||
    (resumePath ? String(resumePath).replace(/^\/+/, "") : null);
  if (
    resumePath &&
    resumeKey &&
    currentKey &&
    resumeKey !== currentKey &&
    resumePath !== currentPath
  ) {
    return {
      unitKey: resumeKey,
      label: resume.label || "Continue learning",
      path: resumePath,
      locked: false,
    };
  }

  return null;
}
/* Code end. Ray using AI Agent. 27/07/2026 */
